import { Trans } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  archiveRecording,
  getArchivalStatus,
  purgeLevel0,
  restoreRecording,
  setArchivalHold,
  type ArchiveScope,
  type ArchivalStatus,
  type ArchivalStatusResponse,
} from "../services/archivalApi";

interface ArchivePanelProps {
  recordingId: string;
  show: boolean;
  onHide: () => void;
  onStatusChange?: (status: ArchivalStatus) => void;
}

function statusBadgeVariant(status: ArchivalStatus): string {
  switch (status) {
    case "level0_archived":
    case "fully_archived":
      return "secondary";
    case "restoring":
      return "warning";
    case "restored":
      return "success";
    case "level0_purged":
      return "danger";
    default:
      return "primary";
  }
}

function statusLabel(status: ArchivalStatus): string {
  switch (status) {
    case "level0_archived":
      return "Level-0 Archived";
    case "fully_archived":
      return "Fully Archived";
    case "restoring":
      return "Restoring…";
    case "restored":
      return "Restored";
    case "level0_purged":
      return "Level-0 Purged";
    default:
      return "Active";
  }
}

const POLL_INTERVAL_MS = 30_000;

/** Format remaining milliseconds into "Xh Ym" or "< 1 min" */
function formatCountdown(ms: number): string {
  if (ms <= 0) return "expired";
  const totalMin = Math.floor(ms / 60_000);
  if (totalMin < 1) return "< 1 min";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function ArchivePanel({
  recordingId,
  show,
  onHide,
  onStatusChange,
}: ArchivePanelProps): React.ReactElement {
  const [statusInfo, setStatusInfo] = useState<ArchivalStatusResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purgeConfirmText, setPurgeConfirmText] = useState("");
  const [holdLoading, setHoldLoading] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [showHoldInput, setShowHoldInput] = useState(false);
  const [restoreCountdown, setRestoreCountdown] = useState<string | null>(null);
  const [selectedArchiveScope, setSelectedArchiveScope] =
    useState<ArchiveScope>("level0");

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getArchivalStatus(recordingId);
      setStatusInfo(data);
      onStatusChange?.(data.archival_status);
    } catch (err) {
      setError("Failed to load archival status");
    } finally {
      setLoading(false);
    }
  }, [recordingId, onStatusChange]);

  // Poll when restoring
  useEffect(() => {
    if (!show) return;
    void fetchStatus();
  }, [show, fetchStatus]);

  useEffect(() => {
    if (!show) {
      return;
    }
    setSelectedArchiveScope("level0");
  }, [show, recordingId]);

  useEffect(() => {
    if (statusInfo?.archival_status !== "restoring") return;
    const interval = setInterval(() => void fetchStatus(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [statusInfo?.archival_status, fetchStatus]);

  // Restore TTL countdown — update every minute
  useEffect(() => {
    const expiresAt = statusInfo?.restore_expires_at
      ? new Date(statusInfo.restore_expires_at).getTime()
      : null;
    if (!expiresAt || statusInfo?.archival_status !== "restored") {
      setRestoreCountdown(null);
      return;
    }
    const tick = () => setRestoreCountdown(formatCountdown(expiresAt - Date.now()));
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [statusInfo?.restore_expires_at, statusInfo?.archival_status]);

  // WebSocket listener for eeg_restore_complete
  useEffect(() => {
    if (!show) return;
    // Re-use any globally injected WS instance — fall back to a no-op if not present
    const ws: { on?: (evt: string, cb: (payload: Record<string, unknown>) => void) => void;
                 off?: (evt: string, cb: unknown) => void } | null =
      (window as unknown as Record<string, unknown>).__eegSocket as typeof ws ?? null;
    if (!ws?.on || !ws?.off) return;
    const handler = (payload: Record<string, unknown>) => {
      if (
        payload.type === "eeg_restore_complete" &&
        payload.recording_id === recordingId
      ) {
        void fetchStatus();
        setSuccessMsg("Recording restored and ready to view.");
      }
    };
    ws.on("message", handler);
    return () => ws.off?.("message", handler);
  }, [show, recordingId, fetchStatus]);

  const handleArchive = async () => {
    setError(null);
    setSuccessMsg(null);
    setActionLoading(true);
    try {
      const result = await archiveRecording(recordingId, selectedArchiveScope);
      if (result.success) {
        setSuccessMsg(
          selectedArchiveScope === "full_bundle"
            ? "Recording archived as a full bundle."
            : selectedArchiveScope === "raw_and_level0"
              ? "Recording archived as a raw plus level-0 bundle."
              : "Recording archived with a level-0 verified copy.",
        );
        void fetchStatus();
      } else {
        setError(result.message);
      }
    } catch {
      setError("Archive request failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async () => {
    setError(null);
    setSuccessMsg(null);
    setActionLoading(true);
    try {
      const result = await restoreRecording(recordingId);
      if (result.data?.success) {
        setSuccessMsg("Recording restored successfully.");
      } else {
        setSuccessMsg(
          result.message ?? "Restore initiated. This may take a few hours.",
        );
      }
      void fetchStatus();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data
          ?.message || "Restore request failed.";
      setError(message);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePurge = async () => {
    setShowPurgeConfirm(false);
    setPurgeConfirmText("");
    setError(null);
    setSuccessMsg(null);
    setActionLoading(true);
    try {
      const result = await purgeLevel0(recordingId);
      if (result.success) {
        setSuccessMsg("Level-0 data permanently purged.");
        void fetchStatus();
      } else {
        setError(result.message);
      }
    } catch {
      setError("Purge request failed.");
    } finally {
      setActionLoading(false);
    }
  };

  const currentStatus = statusInfo?.archival_status ?? null;
  const isArchived =
    currentStatus === "level0_archived" || currentStatus === "fully_archived";
  const isRestoring = currentStatus === "restoring";
  const isPurged = currentStatus === "level0_purged";
  const isRestored = currentStatus === "restored";

  const handleToggleHold = async () => {
    if (!statusInfo) return;
    const currentHold = statusInfo.archival_hold;
    if (!currentHold && !showHoldInput) {
      // Show reason input first
      setShowHoldInput(true);
      return;
    }
    setHoldLoading(true);
    setError(null);
    try {
      const result = await setArchivalHold(
        recordingId,
        !currentHold,
        !currentHold ? holdReason || undefined : undefined,
      );
      if (result.success) {
        setSuccessMsg(
          result.archival_hold
            ? "Archival hold placed. This recording will not be archived automatically."
            : "Archival hold removed.",
        );
        setShowHoldInput(false);
        setHoldReason("");
        void fetchStatus();
      } else {
        setError(result.message);
      }
    } catch {
      setError("Failed to update archival hold.");
    } finally {
      setHoldLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title><Trans ns="features" i18nKey="ui.copy.1f2859912927" /></Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {loading && !statusInfo && (
            <div className="text-center py-3">
              <Spinner animation="border" size="sm" className="me-2" />
              <Trans ns="features" i18nKey="ui.copy.f24f72ddd089" /></div>
          )}

          {statusInfo && (
            <div className="mb-3 d-flex align-items-center gap-2">
              <span className="text-muted"><Trans ns="features" i18nKey="ui.copy.11dc9e195292" /></span>
              <Badge bg={statusBadgeVariant(currentStatus)}>
                {statusLabel(currentStatus)}
              </Badge>
              {isRestoring && <Spinner animation="border" size="sm" />}
            </div>
          )}

          {statusInfo?.archived_at && (
            <p className="small text-muted mb-2">
              <Trans ns="features" i18nKey="ui.copy.e05b9f26c0ec" />{new Date(statusInfo.archived_at).toLocaleString()}
            </p>
          )}

          {statusInfo?.restore_ready_at && (
            <p className="small text-muted mb-2">
              <Trans ns="features" i18nKey="ui.copy.d13d5f7883f0" />{" "}
              {new Date(statusInfo.restore_ready_at).toLocaleString()}
            </p>
          )}

          {/* Restore TTL countdown */}
          {isRestored && restoreCountdown && (
            <Alert
              variant={
                statusInfo?.restore_expires_at &&
                new Date(statusInfo.restore_expires_at).getTime() - Date.now() <
                  2 * 60 * 60 * 1000
                  ? "warning"
                  : "info"
              }
              className="py-2 px-3 small mb-2"
            >
              <Trans ns="features" i18nKey="ui.copy.c3bf1268f748" /><strong>{restoreCountdown}</strong><Trans ns="features" i18nKey="ui.copy.4e7510faa902" /></Alert>
          )}

          {/* Archival hold section */}
          {statusInfo && (
            <div className="border rounded p-2 mb-2">
              <div className="d-flex align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-2">
                  <span className="small fw-semibold"><Trans ns="features" i18nKey="ui.copy.b708303f2d73" /></span>
                  {statusInfo.archival_hold && (
                    <Badge bg="warning" text="dark">
                      <Trans ns="features" i18nKey="ui.copy.a3ce6bd3eeee" /></Badge>
                  )}
                </div>
                <Button
                  variant={
                    statusInfo.archival_hold ? "outline-secondary" : "outline-warning"
                  }
                  size="sm"
                  disabled={holdLoading}
                  onClick={() => void handleToggleHold()}
                >
                  {holdLoading ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : null}
                  {statusInfo.archival_hold ? "Remove Hold" : "Place on Hold"}
                </Button>
              </div>

              {statusInfo.archival_hold && statusInfo.archival_hold_reason && (
                <p className="small text-muted mt-1 mb-0">
                  <Trans ns="features" i18nKey="ui.copy.2c25e64f53a5" />{statusInfo.archival_hold_reason}
                </p>
              )}

              {showHoldInput && (
                <div className="mt-2">
                  <Form.Control
                    size="sm"
                    placeholder="Reason for hold (optional)"
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                    maxLength={500}
                  />
                  <div className="d-flex gap-2 mt-1">
                    <Button
                      size="sm"
                      variant="warning"
                      disabled={holdLoading}
                      onClick={() => void handleToggleHold()}
                    >
                      <Trans ns="features" i18nKey="ui.copy.572b9a69f103" /></Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setShowHoldInput(false);
                        setHoldReason("");
                      }}
                    >
                      <Trans ns="features" i18nKey="ui.copy.77dfd2135f4d" /></Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {successMsg && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setSuccessMsg(null)}
            >
              {successMsg}
            </Alert>
          )}

          {isRestoring && (
            <Alert variant="info">
              <Trans ns="features" i18nKey="ui.copy.f8e42e949613" /></Alert>
          )}

          {!loading &&
            !isArchived &&
            !isRestoring &&
            !isPurged &&
            !isRestored && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold mb-2">
                    <Trans ns="features" i18nKey="ui.copy.4e68629cdc5a" /></Form.Label>
                  <div className="border rounded p-2">
                    <Form.Check
                      type="radio"
                      id="archive-scope-level0"
                      name="archive-scope"
                      className="mb-2"
                      label="Level-0 only"
                      checked={selectedArchiveScope === "level0"}
                      onChange={() => setSelectedArchiveScope("level0")}
                    />
                    <div className="small text-muted ms-4 mb-2">
                      <Trans ns="features" i18nKey="ui.copy.663ec4335b4e" /></div>
                    <Form.Check
                      type="radio"
                      id="archive-scope-raw-and-level0"
                      name="archive-scope"
                      className="mb-2"
                      label="Raw and level-0 bundle"
                      checked={selectedArchiveScope === "raw_and_level0"}
                      onChange={() => setSelectedArchiveScope("raw_and_level0")}
                    />
                    <div className="small text-muted ms-4 mb-2">
                      <Trans ns="features" i18nKey="ui.copy.84c65776b777" /></div>
                    <Form.Check
                      type="radio"
                      id="archive-scope-full-bundle"
                      name="archive-scope"
                      label="Full bundle"
                      checked={selectedArchiveScope === "full_bundle"}
                      onChange={() => setSelectedArchiveScope("full_bundle")}
                    />
                    <div className="small text-muted ms-4">
                      <Trans ns="features" i18nKey="ui.copy.16e4c253ce28" /></div>
                  </div>
                </Form.Group>

                <p className="text-muted small mb-0">
                  <Trans ns="features" i18nKey="ui.copy.ff01a6431999" /></p>
              </>
            )}
        </Modal.Body>

        <Modal.Footer className="d-flex gap-2 justify-content-between">
          <div className="d-flex gap-2">
            {/* Archive button: show when active */}
            {!isArchived && !isRestoring && !isPurged && (
              <Button
                variant="secondary"
                size="sm"
                disabled={actionLoading}
                onClick={() => void handleArchive()}
              >
                {actionLoading ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : null}
                <Trans ns="features" i18nKey="ui.copy.3c8a4e581d66" /></Button>
            )}

            {/* Restore button: show when archived or purged check fails */}
            {(isArchived || isRestored) && !isRestoring && !isPurged && (
              <Button
                variant="outline-primary"
                size="sm"
                disabled={actionLoading || isRestored}
                onClick={() => void handleRestore()}
              >
                {actionLoading ? (
                  <Spinner animation="border" size="sm" className="me-1" />
                ) : null}
                {isRestored ? "Already Restored" : "Restore"}
              </Button>
            )}

            {/* Purge button: show when archived */}
            {isArchived && !isPurged && (
              <Button
                variant="outline-danger"
                size="sm"
                disabled={actionLoading}
                onClick={() => setShowPurgeConfirm(true)}
              >
                <Trans ns="features" i18nKey="ui.copy.51951bd730b5" /></Button>
            )}
          </div>

          <Button variant="outline-secondary" size="sm" onClick={onHide}>
            <Trans ns="features" i18nKey="ui.copy.bbfa773e5a63" /></Button>
        </Modal.Footer>
      </Modal>

      {/* Purge confirmation modal */}
      <Modal
        show={showPurgeConfirm}
        onHide={() => {
          setShowPurgeConfirm(false);
          setPurgeConfirmText("");
        }}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <Trans ns="features" i18nKey="ui.copy.cec7f79f1051" /></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <Trans ns="features" i18nKey="ui.copy.d27f1ed3d423" /><strong><Trans ns="features" i18nKey="ui.copy.abbcb4568a19" /></strong> <Trans ns="features" i18nKey="ui.copy.6f677abb4779" />{" "}
            <strong><Trans ns="features" i18nKey="ui.copy.335a7ae7775d" /></strong>.
          </p>
          <p className="text-muted small">
            <Trans ns="features" i18nKey="ui.copy.9da6b01ab684" /></p>
          <Form.Group>
            <Form.Label className="small fw-semibold">
              <Trans ns="features" i18nKey="ui.copy.3deb74565196" /><code><Trans ns="features" i18nKey="ui.copy.f1ef18630683" /></code> <Trans ns="features" i18nKey="ui.copy.12ef9bcfcd8c" /></Form.Label>
            <Form.Control
              size="sm"
              value={purgeConfirmText}
              onChange={(e) => setPurgeConfirmText(e.target.value)}
              placeholder="PURGE LEVEL 0"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setShowPurgeConfirm(false);
              setPurgeConfirmText("");
            }}
          >
            <Trans ns="features" i18nKey="ui.copy.77dfd2135f4d" /></Button>
          <Button
            variant="danger"
            size="sm"
            disabled={purgeConfirmText !== "PURGE LEVEL 0"}
            onClick={() => void handlePurge()}
          >
            <Trans ns="features" i18nKey="ui.copy.8808120651f8" /></Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
